import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { liberarCaptura, marcarCapturaEnCurso } from '../pwa/actualizacionDelServiceWorker.js';
import {
  borrarBorrador,
  claveDeBorrador,
  describirEdad,
  esquemaDe,
  estaSucio,
  guardarBorrador,
  leerBorrador,
  limpiarBorradoresVencidos,
  RESULTADO,
} from './almacenDeBorradores.js';

/**
 * Engancha un formulario al borrador persistido del dispositivo (RF-084, D-85).
 *
 * LO QUE HACE, Y ES TODO LO QUE HACE:
 *   1. Al abrir, lee el borrador de ESTE formulario. Si está vigente lo restaura y
 *      lo dice (RN-132b); si está vencido lo descarta EN SILENCIO y el formulario
 *      abre limpio (RN-145c, RN-145d).
 *   2. Mientras el formulario está sucio, escribe el borrador conforme se captura
 *      (RN-144e) y declara la captura en curso, con lo que la versión nueva del
 *      service worker queda en espera (RN-130b, RN-131).
 *   3. Sin cambios respecto del valor inicial NO escribe nada y no bloquea nada
 *      (RN-129).
 *   4. Borra el borrador al guardar con éxito y al cancelar explícitamente
 *      (RN-134a, RN-134b). NO lo borra al desmontarse ni al salir: salir conserva
 *      el borrador (RN-130d).
 *
 * LO QUE NO HACE: no envía nada al servidor, no encola, no difiere. Guardar sigue
 * exigiendo conexión (D-02, RNF-041).
 *
 * `almacen` y `ahora` se inyectan para poder ejercitar la caducidad de 24 horas sin
 * esperar 24 horas.
 */
export function useBorrador({
  formulario,
  idRegistro = null,
  idUsuario = null,
  valores,
  valoresIniciales = null,
  alRestaurar,
  almacen,
  ahora,
  activo = true,
}) {
  const clave = useMemo(
    () => claveDeBorrador({ formulario, idRegistro, idUsuario }),
    [formulario, idRegistro, idUsuario],
  );
  const esquema = useMemo(() => (valoresIniciales ? esquemaDe(valoresIniciales) : null), [valoresIniciales]);
  const opciones = useMemo(() => ({ almacen, ahora, esquema }), [almacen, ahora, esquema]);

  const [sucio, setSucio] = useState(false);
  const [restauradoHace, setRestauradoHace] = useState(null);

  const alRestaurarRef = useRef(alRestaurar);
  alRestaurarRef.current = alRestaurar;

  const valoresRef = useRef(valores);
  valoresRef.current = valores;

  // Última fotografía escrita: evita reescribir el borrador —y con ello reiniciar
  // el conteo de 24 h— cuando nada cambió, incluida la restauración misma, que no
  // es una modificación de la persona (RN-145a).
  const ultimaFoto = useRef(null);
  const claveRestaurada = useRef(null);
  const cerrado = useRef(false);

  // RN-145e: los borradores vencidos de otros formularios se limpian también al
  // llegar a un formulario, para que no se acumulen en el dispositivo.
  useEffect(() => {
    limpiarBorradoresVencidos({ almacen, ahora });
  }, [almacen, ahora]);

  // 1. Restauración, una sola vez por formulario y en cuanto se conocen sus
  //    valores iniciales (en edición, cuando llegó el registro del servidor).
  useEffect(() => {
    if (!activo || !valoresIniciales || claveRestaurada.current === clave) return;
    claveRestaurada.current = clave;

    const lectura = leerBorrador(clave, opciones);
    if (lectura.estado !== RESULTADO.VIGENTE) return; // vencido: ya se eliminó, sin avisar.

    ultimaFoto.current = JSON.stringify(lectura.valores);
    alRestaurarRef.current?.(lectura.valores);
    setRestauradoHace(describirEdad(lectura.edadEnMs));
    setSucio(true);
    marcarCapturaEnCurso(clave);
  }, [activo, clave, opciones, valoresIniciales]);

  // 2 y 3. Escritura conforme se captura, y sólo si está sucio.
  useEffect(() => {
    if (!activo || cerrado.current || !valoresIniciales) return;

    const ahoraSucio = estaSucio(valores, valoresIniciales);
    setSucio(ahoraSucio);

    if (!ahoraSucio) {
      // RN-129: sin cambios no hay borrador y no se bloquea la actualización.
      liberarCaptura(clave);
      return;
    }

    const foto = JSON.stringify(valores);
    if (foto !== ultimaFoto.current) {
      ultimaFoto.current = foto;
      guardarBorrador(clave, valores, opciones);
    }
    marcarCapturaEnCurso(clave);
  }, [activo, clave, opciones, valores, valoresIniciales]);

  // Salir a otra aplicación del teléfono: se asegura la última escritura antes de
  // que el sistema pueda descartar la pestaña (RN-132b).
  useEffect(() => {
    if (!activo || typeof document === 'undefined') return undefined;
    const alOcultarse = () => {
      if (document.visibilityState !== 'hidden') return;
      if (cerrado.current || !valoresIniciales) return;
      if (!estaSucio(valoresRef.current, valoresIniciales)) return;
      ultimaFoto.current = JSON.stringify(valoresRef.current);
      guardarBorrador(clave, valoresRef.current, opciones);
    };
    document.addEventListener('visibilitychange', alOcultarse);
    return () => document.removeEventListener('visibilitychange', alOcultarse);
  }, [activo, clave, opciones, valoresIniciales]);

  // Al desmontarse se libera la captura, pero NO se borra el borrador: sólo lo
  // borran los cuatro momentos de RN-134.
  useEffect(() => () => liberarCaptura(clave), [clave]);

  const cerrarBorrador = useCallback(() => {
    cerrado.current = true;
    borrarBorrador(clave, opciones);
    ultimaFoto.current = null;
    setSucio(false);
    setRestauradoHace(null);
    liberarCaptura(clave);
  }, [clave, opciones]);

  /** RN-134a: al guardar con éxito. */
  const borrarPorGuardado = cerrarBorrador;
  /** RN-134b: al cancelar de forma explícita. */
  const borrarPorCancelacion = cerrarBorrador;

  /** "Descartar y empezar limpio": borra el borrador y repone los valores iniciales. */
  const descartarBorrador = useCallback(() => {
    borrarBorrador(clave, opciones);
    ultimaFoto.current = null;
    setRestauradoHace(null);
    setSucio(false);
    liberarCaptura(clave);
    if (valoresIniciales) alRestaurarRef.current?.(valoresIniciales);
  }, [clave, opciones, valoresIniciales]);

  return {
    clave,
    sucio,
    restauradoHace,
    hayBorradorRestaurado: restauradoHace !== null,
    descartarBorrador,
    borrarPorGuardado,
    borrarPorCancelacion,
  };
}
